import { ArrowLeft, ArrowRight, X, Sparkles, Palette, LayoutGrid } from 'lucide-react';
import { useOnboardingStore, UploadedMethodType } from '../../store/onboardingStore';
import { StepProductData } from './steps/StepProductData';
import { StepConfirmScrapedProduct } from './steps/StepConfirmScrapedProduct';
import { ConfirmScrapedProductView } from './ui/ConfirmScrapedProductView';
import { StepSalesAngles } from './steps/StepSalesAngles';
import { StepAdditionalOffers } from './steps/StepAdditionalOffers';
import { StepBrandAndPrice } from './steps/StepBrandAndPrice';
import { StepFunnelCount } from './steps/StepFunnelCount';
import { StepFunnelConfig } from './steps/StepFunnelConfig';
import { StepFunnelSummary } from './steps/StepFunnelSummary';
import { dictionary, Language } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';
import toast, { Toaster } from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { axiosInstanceConversationalEngine } from '../../services/instance';
import { FunnelPayload, MultiFunnelPayload, MultiFunnelProduct, TemplatePage } from '../../services/interface';
import { postFunnel, postBulkFunnels } from '../../services/funnels';
import { creativesService } from '../../feature/ads/cretives/services/creatives.service';
import { userExperimentsService } from '../../services/userExperiments/userExperimentsService';
import { Clock, Users, Shield, Zap, Leaf } from 'lucide-react';
import Loader from '../common/Loader';
import { useTutorialContext } from '../../context/TutorialContext';
import { useNavigate } from 'react-router-dom';
import { Chip } from '../common/chip/Chip';
import { ModalWrapper } from '../modals/ModalWrapper';
import { BuyCreditsModal } from '../modals/BuyCreditsModal';
import { membershipStatsAxios } from '../../services/membership/MembershipAxios';
// import { useTutorialContext } from '../../context/TutorialContext';
// import { TutorialSetup } from '../TutorialSetup';
// import WelcomeModal from '../WelcomeModal';
// import TutorialTooltip from '../TutorialTooltip';


const customToast = (message: string, title: string = 'No pudimos cargar información de la página') => {
  return toast.custom(
    (t) => (
      <div
        className="max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5"
        style={{
          opacity: t.visible ? 1 : 0,
          transform: t.visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'all 0.3s ease-out',
        }}
      >
        <div className="p-4 w-full flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
              <X className="h-5 w-5 text-red-500" />
            </div>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-lg font-medium text-gray-900">
              {title}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {message}
            </p>
          </div>
        </div>
      </div>
    ),
    {
      duration: 4000,
      position: 'top-right',
    }
  );
};

export function OnboardingLayout() {
  const { data, nextStep, previousStep, updateProductData, updateVisualContent, setSalesAngles, updateAdditionalOffers, updateContextData, contextData, hasFetchedSalesAngles, setHasFetchedSalesAngles, resetOnboardingData, setPriceErrors, clearPriceErrors, isCustomSectionsOnly, setShowCustomSectionsToggle, multiFunnelData, initializeFunnels, fetchDesignRecommendation, designRecommendation, isFetchingDesignRecommendation, hasFetchedDesignRecommendation, fetchSalesAngleRecommendation, estimatedCost } = useOnboardingStore();
  const { visualContent, productData } = data;
  const [isLoading, setIsLoading] = useState(false);
  const { language, country } = useAuth();
  const { step } = data;
  const [productCompleteness, setProductCompleteness] = useState<any>(data.additionalOffers);
  const [showConfirmScrapedProduct, setShowConfirmScrapedProduct] = useState(false);
  const [showDesignAnalysis, setShowDesignAnalysis] = useState(false);
  type ScrapedConfirmItem = 'bump' | { type: 'upsell'; index: number };
  const [scrapedConfirmQueueStep2, setScrapedConfirmQueueStep2] = useState<ScrapedConfirmItem[]>([]);
  const { resumeTutorial, setCurrentStepIndex, isPaused } = useTutorialContext();
  const navigate = useNavigate();
  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [endMembershipDate, setEndMembershipDate] = useState("");
  const [loadingCredits, setLoadingCredits] = useState(false);

  useEffect(() => {
    setLoadingCredits(true);
    membershipStatsAxios().then((res) => {
      if (res?.active_membership) {
        setUserCredits(res.active_membership.credits);
        setEndMembershipDate(res.active_membership.ends_at);
      }
      setLoadingCredits(false);
    }).catch(() => setLoadingCredits(false));
  }, []);

  // When design analysis finishes (between step 4b → 5b), initialize funnels and proceed
  useEffect(() => {
    if (showDesignAnalysis && !isFetchingDesignRecommendation && hasFetchedDesignRecommendation) {
      setShowDesignAnalysis(false);
      initializeFunnels(multiFunnelData.funnelCount);
      nextStep();
    }
  }, [showDesignAnalysis, isFetchingDesignRecommendation, hasFetchedDesignRecommendation]);

  // Safety timeout: if recommendation takes too long (15s), proceed anyway
  useEffect(() => {
    if (!showDesignAnalysis) return;
    const timeout = setTimeout(() => {
      if (showDesignAnalysis) {
        setShowDesignAnalysis(false);
        initializeFunnels(multiFunnelData.funnelCount);
        nextStep();
      }
    }, 15000);
    return () => clearTimeout(timeout);
  }, [showDesignAnalysis]);

  // When "Solo secciones personalizadas" toggle is enabled, start fetching design recommendation in background
  useEffect(() => {
    if (isCustomSectionsOnly) {
      fetchDesignRecommendation();
    }
  }, [isCustomSectionsOnly]);

  useEffect(() => {
    if (step === 1 && isPaused) {
      setCurrentStepIndex(1);
      resumeTutorial();
    }
  }, [step]);

  useEffect(() => {
    setProductCompleteness(data.additionalOffers);
  }, [data.additionalOffers]);

  useEffect(() => {
    if (step !== 2) setScrapedConfirmQueueStep2([]);
  }, [step]);

  useEffect(() => {
    const fetchExperiments = async () => {
      try {
        const response = await userExperimentsService.getExperiments();
        const websiteToggle = response.experiments?.website_creation_toggle;
        if (websiteToggle?.enabled === "true") {
          setShowCustomSectionsToggle(true);
        }
      } catch (error) {
        console.error("Error fetching user experiments:", error);
      }
    };
    fetchExperiments();
  }, []);

  // const {
  //   isWelcomeOpen,
  //   isTutorialActive,
  //   currentStep: tutorialStep,
  //   startTutorial,
  //   nextStep: nextTutorialStep,
  //   closeTutorial
  // } = useTutorialContext();

  const currentConfirmStep2 = scrapedConfirmQueueStep2[0];

  const renderStepContent = () => {
    // Show AI design analysis screen between step 4b → 5b while recommendation loads
    if (showDesignAnalysis) {
      return <DesignAnalysisScreen language={language} />;
    }

    if (step === 1 && showConfirmScrapedProduct) {
      return (
        <StepConfirmScrapedProduct
          onBack={() => {
            setShowConfirmScrapedProduct(false);
            updateProductData({
              ...data.productData,
              name: '',
              details: '',
              price: '',
              externalId: null,
              providerId: null,
              variants: null,
            });
            updateVisualContent({ images: [] });
          }}
          onConfirm={() => {
            setShowConfirmScrapedProduct(false);
            fetchSalesAngles();
            nextStep();
          }}
        />
      );
    }

    if (step === 2 && currentConfirmStep2) {
      const bumpOffer = data.additionalOffers.bumpOffer;
      const upsellProducts = data.additionalOffers.upsell?.products ?? [];

      if (currentConfirmStep2 === 'bump' && bumpOffer) {
        const bumpImages = bumpOffer.url_images ? [bumpOffer.url_images] : [];
        return (
          <ConfirmScrapedProductView
            title={dictionary['confirm-order-bump']?.[language] ?? 'Confirma tu Order Bump'}
            subtitle={dictionary['edit-data-if-needed']?.[language]}
            name={bumpOffer.productName || ''}
            description={bumpOffer.description || ''}
            images={bumpImages}
            onNameChange={(value) =>
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: { ...bumpOffer, productName: value },
              })
            }
            onDescriptionChange={(value) =>
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: { ...bumpOffer, description: value },
              })
            }
            onImagesUpdate={(images) =>
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: { ...bumpOffer, url_images: images[0] ?? '' },
              })
            }
            onBack={() => {
              setScrapedConfirmQueueStep2([]);
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: {
                  ...bumpOffer,
                  productName: '',
                  description: '',
                  url_images: '',
                  image: '',
                  externalId: null,
                  providerId: null,
                  isManualEntry: false,
                  uploadedMethod: UploadedMethodType.LINK,
                },
                upsell: {
                  ...data.additionalOffers.upsell!,
                  products: upsellProducts.map((p) => ({
                    ...p,
                    productName: '',
                    description: '',
                    url_images: [],
                    image: undefined,
                    externalId: null,
                    providerId: null,
                    isManualEntry: false,
                    uploadedMethod: UploadedMethodType.LINK,
                  })),
                },
              });
            }}
            onConfirm={() => {
              const nextQueue = scrapedConfirmQueueStep2.slice(1);
              setScrapedConfirmQueueStep2(nextQueue);
              if (nextQueue.length === 0) nextStep();
            }}
            maxImages={1}
          />
        );
      }

      if (currentConfirmStep2 !== 'bump' && upsellProducts[currentConfirmStep2.index]) {
        const upsellIndex = currentConfirmStep2.index;
        const product = upsellProducts[upsellIndex];
        const productImages = product.url_images ?? [];
        const imagesArray = Array.isArray(productImages) ? productImages : [productImages].filter(Boolean);
        return (
          <ConfirmScrapedProductView
            title={dictionary['confirm-upsell']?.[language] ?? 'Confirma tu Upsell'}
            subtitle={dictionary['edit-data-if-needed']?.[language]}
            name={product.productName || ''}
            description={product.description || ''}
            images={imagesArray}
            onNameChange={(value) => {
              const next = [...upsellProducts];
              next[upsellIndex] = { ...product, productName: value };
              updateAdditionalOffers({
                ...data.additionalOffers,
                upsell: { ...data.additionalOffers.upsell!, products: next },
              });
            }}
            onDescriptionChange={(value) => {
              const next = [...upsellProducts];
              next[upsellIndex] = { ...product, description: value };
              updateAdditionalOffers({
                ...data.additionalOffers,
                upsell: { ...data.additionalOffers.upsell!, products: next },
              });
            }}
            onImagesUpdate={(images) => {
              const next = [...upsellProducts];
              next[upsellIndex] = { ...product, url_images: images };
              updateAdditionalOffers({
                ...data.additionalOffers,
                upsell: { ...data.additionalOffers.upsell!, products: next },
              });
            }}
            onBack={() => {
              setScrapedConfirmQueueStep2([]);
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: data.additionalOffers.bumpOffer
                  ? {
                      ...data.additionalOffers.bumpOffer,
                      productName: '',
                      description: '',
                      url_images: '',
                      image: '',
                      externalId: null,
                      providerId: null,
                      isManualEntry: false,
                      uploadedMethod: UploadedMethodType.LINK,
                    }
                  : undefined,
                upsell: {
                  ...data.additionalOffers.upsell!,
                  products: upsellProducts.map((p) => ({
                    ...p,
                    productName: '',
                    description: '',
                    url_images: [],
                    image: undefined,
                    externalId: null,
                    providerId: null,
                    isManualEntry: false,
                    uploadedMethod: UploadedMethodType.LINK,
                  })),
                },
              });
            }}
            onConfirm={() => {
              const nextQueue = scrapedConfirmQueueStep2.slice(1);
              setScrapedConfirmQueueStep2(nextQueue);
              if (nextQueue.length === 0) nextStep();
            }}
          />
        );
      }
    }

    switch (step) {
      case 1:
        return <StepProductData isScraping={isLoading} />;
      case 2:
        return <StepAdditionalOffers isScraping={isLoading} />;
      case 3:
        return <StepBrandAndPrice />;
      case 4:
        return isCustomSectionsOnly ? <StepFunnelCount /> : <StepSalesAngles />;
      case 5:
        return <StepFunnelConfig onComplete={handleContinue} onBack={previousStep} />;
      case 6:
        return <StepFunnelSummary />;
      default:
        return <StepProductData />;
    }
  };

  const handleProductScrape = async () => {
    if (!data.productData.productLink) {
      return true;
    }

    setIsLoading(true);
    let success = false;

    try {
      const response = await axiosInstanceConversationalEngine.post('/scrape-product', {
        product_url: data.productData.productLink,
        country: country
      });

      const productData = response.data.data;

      const hasName = productData.name && productData.name !== "unknown";
      const hasDescription = productData.description && productData.description !== "unknown";
      const hasImages = productData.images && productData.images.length > 0;

      if (!hasName && !hasDescription && !hasImages) {
        customToast(dictionary["product-not-found"][language], dictionary["product-not-found-title"][language]);
        return false;
      }

      let variants = null;
      if (productData.provider_id === 'dropi') {
        variants = productData.variants || null;
      }

      updateProductData({
        ...data.productData,
        name: hasName ? productData.name : '',
        price: productData?.external_sell_price?.toString() || '0',
        details: hasDescription ? productData.description : '',
        isManualEntry: false,
        externalId: productData.external_id,
        providerId: productData.provider_id,
        variants: variants
      });

      if (productData.images && productData.images.length > 0) {
        updateVisualContent({
          images: productData.images
        });
      }

      success = true;
    } catch (err) {
      console.error('Error fetching product:', err);
      customToast(dictionary["failed-to-fetch-product-data"][language], dictionary["failed-to-fetch-product-data-title"][language]);
      success = false;
    } finally {
      setIsLoading(false);
    }

    return success;
  };

  const maxStep = isCustomSectionsOnly ? 6 : 4;
  const isLastStep = step === maxStep;

  const requiredCredits = isCustomSectionsOnly
    ? multiFunnelData.funnels.length * 85
    : visualContent.selectedAngle.length * 85;

  const validateCredits = (): boolean => {
    if (userCredits < requiredCredits) {
      setIsOpenBuyCreditsModal(true);
      return false;
    }
    return true;
  };

  const handleCreditsRefreshed = (newCredits: number) => {
    setUserCredits(newCredits);
  };

  const handleProductCompleteness = (data: any) => {
    updateAdditionalOffers(data);
    setProductCompleteness(data);
  }

  interface FetchSalesAnglesProps {
    productName?: string;
    productDescription?: string;
  }

  const fetchSalesAngles = async ({ productName = '', productDescription = '' }: FetchSalesAnglesProps = {}) => {
    if (hasFetchedSalesAngles) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosInstanceConversationalEngine.post('/resolve-info-funnel', {
        product_name: productData?.name || productName,
        product_description: productData?.details || productDescription,
        language: language,
      });
      // const response = {
      //   data: {
      //     sales_angles: [
      //       { id: '1', name: 'Angle 1', description: 'Description 1', },
      //       { id: '2', name: 'Angle 2', description: 'Description 2' },
      //       { id: '3', name: 'Angle 3', description: 'Description 3' },
      //     ],
      //     pain_detection: 'Pain detection',
      //     buyer_detection: 'Buyer detection',
      //   }
      // };

      if (response.data && Array.isArray(response.data.sales_angles) && response.data.sales_angles.length > 0) {
        const apiSalesAngles = response.data.sales_angles.map((angle: any, index: number) => {
          const iconMap = [Clock, Users, Shield, Zap, Leaf];
          const id = angle.name ? angle.name.toLowerCase().replace(/\s+/g, '-') : `angle-${index}`;

          return {
            id,
            title: angle.name || `Angle ${index + 1}`,
            description: angle.description || 'No description available',
            icon: iconMap[index % iconMap.length],
            isRecommended: index === 0
          };
        });

        updateContextData({
          pain_detection: response.data.pain_detection || '',
          buyer_detection: response.data.buyer_detection || '',
          recommended_sales_angles: response.data.sales_angles || [],
        });

        setSalesAngles(apiSalesAngles);

        // Fire AI angle selection — raw angles from API have { name, description }
        fetchSalesAngleRecommendation(response.data.sales_angles);
      }
    } catch (error) {
      console.error("Error fetching sales angles:", error);
      updateContextData({
        pain_detection: 'Error fetching data.',
        buyer_detection: 'Error fetching data.',
        recommended_sales_angles: [],
      });
    } finally {
      setIsLoading(false);
      setHasFetchedSalesAngles(true);
    }
  };

  const handleContinue = async () => {
    if (step === 1) {
      if (data.productData.productLink &&
        (!data.productData.name || !data.productData.details || !data.visualContent.images?.length)) {
        const scrapingSuccessful = await handleProductScrape();

        if (!scrapingSuccessful) return;
        setShowConfirmScrapedProduct(true);
        return;
      }

      if (!data.productData.name || !data.productData.details || (!data.visualContent.images?.length && !data.visualContent.imagesBase64?.length)) {
        customToast(dictionary["product-information-incomplete"][language], dictionary["product-information-incomplete-title"][language]);
        return;
      }

      fetchSalesAngles();
      nextStep();
    } else if (step === 2) {
      const shouldScrapeBumpOffer = data.additionalOffers.bumpOffer?.enabled && data.additionalOffers.bumpOffer?.productLink && data.additionalOffers.bumpOffer?.uploadedMethod === UploadedMethodType.LINK;

      const upsellEnabled = data.additionalOffers.upsell?.enabled &&
        data.additionalOffers.upsell.products?.some(p =>
          p.productLink || (p.productName && p.price && p.description && p.image)
        );


      setIsLoading(true);

      try {
        const promises = [];

        if (shouldScrapeBumpOffer &&
          (!data.additionalOffers.bumpOffer?.isManualEntry ||
            (!data.additionalOffers.bumpOffer.productName &&
              !data.additionalOffers.bumpOffer.price &&
              !data.additionalOffers.bumpOffer.description))) {

          const bumpOfferPromise = axiosInstanceConversationalEngine.post('/scrape-product', {
            product_url: data.additionalOffers.bumpOffer?.productLink || ''
          }).then(response => {
            return { type: 'bumpOffer', data: response.data.data };
          });

          promises.push(bumpOfferPromise);
        }

        if (upsellEnabled) {
          const upsellPromises = data.additionalOffers.upsell.products
            .filter(product => product.productLink && !product.isManualEntry)
            .map((product, index) => {
              return axiosInstanceConversationalEngine.post('/scrape-product', {
                product_url: product.productLink
              }).then(response => {
                return {
                  type: 'upsell',
                  index: index,
                  originalProduct: product,
                  data: response.data.data
                };
              });
            });

          promises.push(...upsellPromises);
        }

        const results = await Promise.all(promises);

        const updatedAdditionalOffersNew = { ...data.additionalOffers };
        const updatedUpsellProducts = [...data.additionalOffers.upsell.products];

        results.forEach(result => {
          if (result.type === 'bumpOffer' && data.additionalOffers.bumpOffer) {
            const productData = result.data;
            updatedAdditionalOffersNew.bumpOffer = {
              ...data.additionalOffers.bumpOffer,
              productName: productData.name || '',
              price: productData.external_sell_price ? productData.external_sell_price.toString() : '0',
              description: productData.description && productData.description !== 'unknown' ? productData.description : '',
              url_images: productData.images && productData.images.length > 0 ? productData.images[0] : '',
              image: '',
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          } else if (result.type === 'upsell') {
            const productData = result.data;
            updatedUpsellProducts[(result as any).index] = {
              ...(result as any).originalProduct,
              productName: productData.name,
              price: productData.external_sell_price.toString(),
              description: productData.description && productData.description !== 'unknown' ? productData.description : '',
              image: '',
              url_images: productData.images?.[0] ? [productData.images[0]] : [],
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          }
        });

        if (upsellEnabled) {
          updatedAdditionalOffersNew.upsell = {
            ...data.additionalOffers.upsell,
            products: updatedUpsellProducts
          };
        }

        handleProductCompleteness(updatedAdditionalOffersNew);

        const confirmQueue: ScrapedConfirmItem[] = [];
        results.forEach((result: any) => {
          if (result.type === 'bumpOffer') confirmQueue.push('bump');
          if (result.type === 'upsell') confirmQueue.push({ type: 'upsell', index: result.index });
        });

        if (confirmQueue.length > 0) {
          setScrapedConfirmQueueStep2(confirmQueue);
          setIsLoading(false);
          return;
        }

        if (updatedAdditionalOffersNew.bumpOffer?.enabled) {
          const bumpOffer = updatedAdditionalOffersNew.bumpOffer;
          if (!bumpOffer.productName || !bumpOffer.description || (!bumpOffer.image && !bumpOffer.url_images)) {
            customToast(dictionary["order-bump-information-incomplete"][language]);
            setIsLoading(false);
            return;
          }
        }

        if (updatedAdditionalOffersNew.upsell?.enabled && updatedAdditionalOffersNew.upsell.products?.length > 0) {
          for (const product of updatedAdditionalOffersNew.upsell?.products) {
            if (!product.productName || !product.price || !product.description || (!product.image && !((product.url_images ?? []).length > 0))) {
              customToast(dictionary["upsell-information-incomplete"][language]);
              setIsLoading(false);
              return;
            }
          }
        }

        nextStep();
      } catch (err) {
        console.error('Error fetching product data:', err);
        customToast(dictionary["failed-to-fetch-product-data"][language], dictionary["failed-to-fetch-product-data-title"][language]);
      } finally {
        setIsLoading(false);
      }
    } else if (step === 3) {
      const newPriceErrors: { main?: string; bump?: string; upsell?: string } = {};

      if (!data.productData.price || !data.productData.price.trim() || parseFloat(data.productData.price) <= 0) {
        newPriceErrors.main = dictionary["price-required"][language];
      }
      if (data.additionalOffers.bumpOffer?.enabled) {
        if (!data.additionalOffers.bumpOffer?.price || !data.additionalOffers.bumpOffer?.price.trim() || parseFloat(data.additionalOffers.bumpOffer?.price) <= 0) {
          newPriceErrors.bump = dictionary["price-required"][language];
        }
      }
      if (data.additionalOffers.upsell?.enabled) {
        if (data.additionalOffers.upsell?.products?.some(product => !product.price || !product.price.trim() || parseFloat(product.price) <= 0)) {
          newPriceErrors.upsell = dictionary["price-required"][language];
        }
      }

      if (Object.keys(newPriceErrors).length > 0) {
        setPriceErrors(newPriceErrors);
        return;
      }

      clearPriceErrors();
      nextStep();
    } else if (step === 4) {
      if (isCustomSectionsOnly) {
        // Multi-funnel flow: if recommendation is still loading, show analysis screen
        if (isFetchingDesignRecommendation) {
          setShowDesignAnalysis(true);
        } else {
          // Recommendation already done (or failed), proceed directly
          initializeFunnels(multiFunnelData.funnelCount);
          nextStep();
        }
      } else {
        // Original flow: validate sales angles
        if (!data.visualContent.customAngle && !data.visualContent.selectedAngle || data.visualContent.selectedAngle.length < 1) {
          customToast(dictionary["please-select-a-minimum-of-1-marketing-angle"][language], dictionary["marketing-angle-required"][language]);
          return;
        }
        nextStep();
      }
    } else if (step === 5) {
      // Funnel config step: validate each funnel has a design selected
      const funnelWithoutDesign = multiFunnelData.funnels.find(f => !f.designTemplate);
      if (funnelWithoutDesign) {
        customToast(
          dictionary["please-select-design-for-all-funnels"]?.[language] ?? "Todos los embudos deben tener un diseño seleccionado",
          dictionary["design-required"]?.[language] ?? "Diseño requerido"
        );
        return;
      }
      // Validate each funnel has at least 1 angle selected
      const invalidFunnel = multiFunnelData.funnels.find(f => f.selectedAngles.length === 0);
      if (invalidFunnel) {
        customToast(
          dictionary["please-select-a-minimum-of-1-marketing-angle"]?.[language] ?? "Please select at least 1 angle per funnel",
          dictionary["marketing-angle-required"]?.[language] ?? "Angle required"
        );
        return;
      }
      // Debug: preview full payload before going to summary
      {
        const previewProducts: any[] = [{
          external_id: data.productData.externalId || null,
          provider_id: data.productData.providerId || null,
          name: data.productData.name,
          description: data.productData.details,
          price: parseFloat(data.productData.price),
          url_images: data.visualContent.images || [],
          images: data.visualContent.imagesBase64 || [],
          type: 'PRINCIPAL',
          variants: data.productData.variants || null,
        }];
        if (productCompleteness?.upsell?.enabled && productCompleteness.upsell.products?.length > 0) {
          for (const p of productCompleteness.upsell.products) {
            if (p.productName && p.price) {
              previewProducts.push({
                external_id: p.externalId || null,
                provider_id: p.providerId || null,
                name: p.productName,
                description: p.description || '',
                price: parseFloat(p.price) || 0,
                url_images: p.url_images || [],
                type: 'UP_SELL',
              });
            }
          }
        }
        if (productCompleteness?.bumpOffer?.enabled && productCompleteness.bumpOffer.productName && productCompleteness.bumpOffer.price) {
          previewProducts.push({
            external_id: productCompleteness.bumpOffer.externalId || null,
            provider_id: productCompleteness.bumpOffer.providerId || null,
            name: productCompleteness.bumpOffer.productName,
            description: productCompleteness.bumpOffer.description || '',
            price: parseFloat(productCompleteness.bumpOffer.price) || 0,
            url_images: productCompleteness.bumpOffer.url_images ? [productCompleteness.bumpOffer.url_images] : [],
            type: 'BUMP_OFFER',
          });
        }
        console.log('🔍 Preview multi-funnel payload:', JSON.stringify({
          products: previewProducts,
          funnels: multiFunnelData.funnels.map(funnel => ({
            brand_name: funnel.name || data.visualStyle.brandName || '',
            sale_angles: funnel.selectedAngles,
            free_shipping: data.productData.freeShipping,
            warranties: data.productData.warranty === 'none' ? 0 : data.productData.warranty === 'custom' ? parseInt(data.productData.customWarrantyDays) : 30,
            template_structure: {
              template_pages: buildTemplatePages(funnel),
            },
          })),
          data_for_context: {
            pain_detection: contextData.pain_detection,
            buyer_detection: contextData.buyer_detection,
            recommended_sales_angles: contextData.recommended_sales_angles,
          },
        }, null, 2));
      }
      nextStep();
    } else {
      nextStep();
    }
  };

  const handleSubmit = async () => {
    // Credit validation gate
    if (!validateCredits()) return;

    setIsLoading(true);
    try {
      if (parseFloat(data.productData.price) === 0) {
        customToast('The main product price cannot be 0.', 'Main Product Price Incomplete');
        setIsLoading(false);
        return;
      }

      const products: any = [
        {
          external_id: data.productData.externalId || null,
          provider_id: data.productData.providerId || null,
          name: data.productData.name,
          description: data.productData.details,
          price: parseFloat(data.productData.price),
          url_images: data.visualContent.images || [],
          images: data.visualContent.imagesBase64 || [],
          type: "PRINCIPAL",
          variants: data.productData.variants || null
        }
      ];

      let upsellCount = 1;
      if (productCompleteness && productCompleteness.upsell?.enabled && productCompleteness.upsell.products?.length > 0) {
        productCompleteness.upsell.products.forEach((product: any) => {
          if (parseFloat(product.price) === 0) {
            customToast('The upsell product price cannot be 0.', 'Upsell Product Price Incomplete');
            setIsLoading(false);
            return;
          }

          if (product.productName && product.price) {
            products.push({
              external_id: product.externalId || null,
              provider_id: product.providerId || null,
              name: product.productName,
              description: product.description || "",
              price: parseFloat(product.price) || 0,
              url_images: product.url_images || [],
              // images: product.image ? [product.image] : [],
              type: "UP_SELL"
            });
            upsellCount++;
          }
        });
      }

      if (productCompleteness && productCompleteness.bumpOffer?.enabled && productCompleteness.bumpOffer.productName && productCompleteness.bumpOffer.price) {
        if (parseFloat(productCompleteness.bumpOffer.price) === 0) {
          customToast('The order bump price cannot be 0.', 'order bump Price Incomplete');
          setIsLoading(false);
          return;
        }

        products.push({
          external_id: productCompleteness.bumpOffer.externalId || null,
          provider_id: productCompleteness.bumpOffer.providerId || null,
          name: productCompleteness.bumpOffer.productName,
          description: productCompleteness.bumpOffer.description || "",
          price: parseFloat(productCompleteness.bumpOffer.price) || 0,
          url_images: productCompleteness.bumpOffer.url_images ? [productCompleteness.bumpOffer.url_images] : [],
          // images: productCompleteness.bumpOffer.image ? [productCompleteness.bumpOffer.image] : [],
          type: "BUMP_OFFER"
        });
      }

      const warrantyDays = data.productData.warranty === 'none' ? 0 :
        data.productData.warranty === 'custom' ?
          parseInt(data.productData.customWarrantyDays) : 30;

      const payload: FunnelPayload = {
        products: products,
        brand_name: data.visualStyle.brandName || "",
        sale_angles: data.visualContent.selectedAngle,
        free_shipping: data.productData.freeShipping,
        warranties: warrantyDays,
        template_design: {
          theme: data.visualContent.selectedAngle[0].name || "default",
          primary_color: data.visualStyle.colorPalette || "#FFFFFF",
          secondary_color: "#FFFFFF"
        },
        data_for_context: {
          pain_detection: contextData.pain_detection,
          buyer_detection: contextData.buyer_detection,
          recommended_sales_angles: contextData.recommended_sales_angles
        },
      };

      await postFunnel(payload);
      resetOnboardingData();
      navigate("/funnels");
      // window.location.href = window.location.origin + "/funnels"

    } catch (error) {
      console.error('Error submitting form:', error);
      customToast('There was an error creating your funnels. Please try again.', 'Funnels Creation Failed');
      setIsLoading(false);
    }
  };

  const STANDARD_SECTION_MAP: Record<string, { type: string; pageId: string }> = {
    'faq': { type: 'faqSection', pageId: 'faqs' },
    'cta-1': { type: 'buyNowFreeShippingSection', pageId: 'buy_now_free_shipping' },
    'cta-2': { type: 'buyNowFreeShippingSection', pageId: 'buy_now_free_shipping' },
    'cta-3': { type: 'buyNowFreeShippingSection', pageId: 'buy_now_free_shipping' },
    'warranty': { type: 'guaranteeSection', pageId: 'guarantee_section' },
  };

  const buildTemplatePages = (funnel: typeof multiFunnelData.funnels[0]): TemplatePage[] => {
    const pages: TemplatePage[] = [];
    let sortIndex = 0;

    const templateType = funnel.designTemplateType || 'default';

    const enabledSections = funnel.sections
      .filter(s => s.enabled)
      .sort((a, b) => a.order - b.order);

    let ctaCount = 1;

    for (const section of enabledSections) {
      if (!section.isStandard && section.sectionType) {
        const sectionUid = `custom_image_section_${crypto.randomUUID()}`;
        pages.push({
          id: sectionUid,
          name: sectionUid,
          type: 'customImageSection',
          sort: sortIndex++,
          page: 'productPage',
          source: 'generate_image',
          metadata: {
            type_section_image: section.sectionType,
            template_id: templateType,
          },
        });
      } else if (section.isStandard) {
        const mapping = STANDARD_SECTION_MAP[section.id];
        if (!mapping) continue;

        if (mapping.type === 'buyNowFreeShippingSection') {
          ctaCount++;
          pages.push({
            id: `${mapping.pageId}_${ctaCount}`,
            name: `${mapping.pageId}_${ctaCount}`,
            type: mapping.type,
            sort: sortIndex++,
            page: 'productPage',
          });
        } else {
          pages.push({
            id: mapping.pageId,
            name: mapping.pageId,
            type: mapping.type,
            sort: sortIndex++,
            page: 'productPage',
          });
        }
      }
    }

    // Always add thankyou page sections
    pages.push(
      { id: 'header_thankyou', name: 'header_thankyou', type: 'headerThankyouSection', sort: 0, page: 'thankyouPage' },
      { id: 'product_list', name: 'product_list', type: 'productListSection', sort: 1, page: 'thankyouPage' },
      { id: 'button_thankyou', name: 'button_thankyou', type: 'buttonThankyouSection', sort: 2, page: 'thankyouPage' },
    );

    return pages;
  };

  const handleSubmitMultiFunnel = async () => {
    // Credit validation gate
    if (!validateCredits()) return;

    setIsLoading(true);
    try {
      if (parseFloat(data.productData.price) === 0) {
        customToast('The main product price cannot be 0.', 'Main Product Price Incomplete');
        setIsLoading(false);
        return;
      }

      // Build products array (principal + upsell + bump)
      const products: MultiFunnelProduct[] = [
        {
          external_id: data.productData.externalId || null,
          provider_id: data.productData.providerId || null,
          name: data.productData.name,
          description: data.productData.details,
          price: parseFloat(data.productData.price),
          url_images: data.visualContent.images || [],
          images: data.visualContent.imagesBase64 || [],
          type: 'PRINCIPAL',
          variants: data.productData.variants || null,
        },
      ];

      // Add upsell products
      if (productCompleteness?.upsell?.enabled && productCompleteness.upsell.products?.length > 0) {
        for (const product of productCompleteness.upsell.products) {
          if (product.productName && product.price) {
            products.push({
              external_id: product.externalId || null,
              provider_id: product.providerId || null,
              name: product.productName,
              description: product.description || '',
              price: parseFloat(product.price) || 0,
              url_images: product.url_images || [],
              type: 'UP_SELL',
            });
          }
        }
      }

      // Add bump offer
      if (productCompleteness?.bumpOffer?.enabled && productCompleteness.bumpOffer.productName && productCompleteness.bumpOffer.price) {
        products.push({
          external_id: productCompleteness.bumpOffer.externalId || null,
          provider_id: productCompleteness.bumpOffer.providerId || null,
          name: productCompleteness.bumpOffer.productName,
          description: productCompleteness.bumpOffer.description || '',
          price: parseFloat(productCompleteness.bumpOffer.price) || 0,
          url_images: productCompleteness.bumpOffer.url_images ? [productCompleteness.bumpOffer.url_images] : [],
          type: 'BUMP_OFFER',
        });
      }

      const warrantyDays = data.productData.warranty === 'none' ? 0 :
        data.productData.warranty === 'custom' ?
          parseInt(data.productData.customWarrantyDays) : 30;

      const brandName = data.visualStyle.brandName || '';

      // Build funnels array with template_structure per funnel
      const funnels = multiFunnelData.funnels.map(funnel => ({
        brand_name: funnel.name || brandName,
        sale_angles: funnel.selectedAngles,
        free_shipping: data.productData.freeShipping,
        warranties: warrantyDays,
        template_structure: {
          template_pages: buildTemplatePages(funnel),
        },
      }));

      const payload: MultiFunnelPayload = {
        products,
        funnels,
        data_for_context: {
          pain_detection: contextData.pain_detection,
          buyer_detection: contextData.buyer_detection,
          recommended_sales_angles: contextData.recommended_sales_angles,
        },
      };

      console.log('Multi-funnel payload:', JSON.stringify(payload, null, 2));

      const bulkResponse = await postBulkFunnels(payload);

      // Fire async creative generation for funnels with selected ad templates
      // Must happen BEFORE resetOnboardingData() which clears the state
      const { adTemplates, sectionTemplates } = useOnboardingStore.getState();

      for (let i = 0; i < multiFunnelData.funnels.length; i++) {
        const funnel = multiFunnelData.funnels[i];
        const selectedTemplateIds = funnel.selectedAdTemplates || [];
        if (selectedTemplateIds.length === 0) continue;

        const websiteId = bulkResponse[i]?.id;
        if (!websiteId) {
          console.warn(`No websiteId found for funnel index ${i}, skipping creative generation`);
          continue;
        }

        // Get colors from the funnel's selected design template (section templates)
        const matchingSectionTemplate = sectionTemplates.find(
          t => t.type === funnel.designTemplateType
        );
        const primaryColor = (matchingSectionTemplate?.metadata as any)?.primary_color ?? null;
        const backgroundColor = (matchingSectionTemplate?.metadata as any)?.background_color ?? null;

        // Resolve full AdStyle objects for selected template IDs
        const selectedAdStyles = selectedTemplateIds
          .map(id => adTemplates.find(t => t.id === id))
          .filter((t): t is NonNullable<typeof t> => !!t);

        const templates = selectedAdStyles.map(template => ({
          user_prompt: '',
          image_template: template.image_url || template.coverImage,
          format: '1:1' as const,
          template_id: template.name_id || '',
          color_mode: 'custom' as const,
          primary_color: primaryColor,
          background_color: backgroundColor,
          reference_type: 'template' as const,
          config: { language },
          ...(funnel.selectedAngles.length > 0
            ? { angles: funnel.selectedAngles[0].name }
            : {}),
        }));

        // Fire-and-forget — don't block navigation
        creativesService.generateCreativesAsync(websiteId, {
          product_name: data.productData.name,
          product_image: data.visualContent.images?.[0] || '',
          product_description: data.productData.details,
          templates,
        }).catch(err => {
          console.error(`Error generating creatives for funnel ${funnel.name}:`, err);
        });
      }

      resetOnboardingData();
      navigate('/funnels');
    } catch (error) {
      console.error('Error submitting multi-funnel:', error);
      customToast('There was an error creating your funnels. Please try again.', 'Funnels Creation Failed');
      setIsLoading(false);
    }
  };

  const stepIsDisabled = step === 3 && data.visualStyle.brandName === '';
  const isButtonDisabled = stepIsDisabled || isLoading;

  return (
    <>
      {/* <TutorialSetup />
      <WelcomeModal
        isOpen={isWelcomeOpen}
        onStart={startTutorial}
        onClose={closeTutorial}
      />
      <TutorialTooltip
        step={tutorialStep}
        onNext={nextTutorialStep}
        onClose={closeTutorial}
        isActive={isTutorialActive}
      /> */}
      <Toaster position="top-right" />
      {isOpenBuyCreditsModal && (
        <ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={() => setIsOpenBuyCreditsModal(false)}>
          <BuyCreditsModal
            onClose={() => setIsOpenBuyCreditsModal(false)}
            nextRenewalDate={endMembershipDate}
            language={language}
            openInNewTab={true}
            onCreditsRefreshed={handleCreditsRefreshed}
          />
        </ModalWrapper>
      )}
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4 relative">
        <div className="fixed top-6 right-6 z-50">
          <Chip
            text={dictionary["credits"][language]}
            value={userCredits.toString()}
            onClick={() => setIsOpenBuyCreditsModal(true)}
            loading={loadingCredits}
          />
        </div>
        <div className={`w-full ${step === 5 && isCustomSectionsOnly ? 'max-w-4xl' : 'max-w-2xl'} transition-all duration-300`}>
          <div className={`bg-white shadow-xl ${step === 5 && isCustomSectionsOnly ? 'rounded-[40px] p-6 md:p-8' : 'rounded-[50px] p-8 md:p-12'}`}>
            {renderStepContent()}

          {!showDesignAnalysis && !(step === 1 && showConfirmScrapedProduct) && !(step === 2 && scrapedConfirmQueueStep2.length > 0) && !(step === 5 && isCustomSectionsOnly) && (
          <div className={`${step === 1 ? 'flex justify-end items-end' : 'flex justify-between items-center'} mt-8`}>
            {step > 1 && (
              <button
                onClick={previousStep}
                className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                {dictionary["back"][language]}
              </button>
            )}

            <div className="flex items-center justify-end gap-4">
              {isLastStep && !isCustomSectionsOnly && (
                <div className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#F1F5F9] rounded-full text-xs text-[#64748B]">
                  <span className="font-semibold text-gray-600">{dictionary["cost"][language]}:</span>
                  <span className="font-bold text-gray-700">{visualContent.selectedAngle.length * 85} {dictionary["credits"][language]}</span>
                </div>
              )}
              {isLastStep && isCustomSectionsOnly && (
                <div className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#F1F5F9] rounded-full text-xs text-[#64748B]">
                  <span className="font-semibold text-gray-600">{dictionary["cost"][language]}:</span>
                  <span className="font-bold text-gray-700">{estimatedCost?.total_cost ?? (multiFunnelData.funnels.length * 85)} {dictionary["credits"][language]}</span>
                </div>
              )}

              <button
                disabled={isButtonDisabled}
                onClick={isLastStep ? (isCustomSectionsOnly ? handleSubmitMultiFunnel : handleSubmit) : handleContinue}
                className={`${isButtonDisabled ? 'bg-[#E2E8F0]' : isLastStep && isCustomSectionsOnly ? 'bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] shadow-lg shadow-purple-200' : 'bg-[#3E3EFF] hover:bg-[#2d3a7f]'} ml-auto flex items-center gap-2 px-7 py-2.5 text-sm text-white rounded-full font-medium transition-all`}
              >
                {isLoading ? <Loader size={20} color="#FFFFFF" /> : null}
                {isLastStep
                  ? isCustomSectionsOnly
                    ? (dictionary["generate"]?.[language] ?? "Generar")
                    : dictionary["create-page"][language]
                  : dictionary["continue"][language]
                }
                {!isLastStep && <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

// ─── Design Analysis Loading Screen ──────────────────────────
function DesignAnalysisScreen({ language }: { language: Language }) {
  const [activeStep, setActiveStep] = useState(0);

  const analysisSteps = [
    {
      icon: Sparkles,
      label: dictionary["analyzing-product"]?.[language] ?? "Analizando tu producto...",
    },
    {
      icon: Palette,
      label: dictionary["selecting-design"]?.[language] ?? "Seleccionando el mejor diseño...",
    },
    {
      icon: LayoutGrid,
      label: dictionary["optimizing-sections"]?.[language] ?? "Optimizando las secciones...",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % analysisSteps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      {/* Animated AI icon */}
      <div className="relative mb-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#6366F1] to-[#3E3EFF] flex items-center justify-center animate-pulse">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <div className="absolute inset-0 w-20 h-20 rounded-full bg-gradient-to-br from-[#6366F1] to-[#3E3EFF] opacity-20 animate-ping" />
      </div>

      {/* Title */}
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-2">
        {dictionary["fluxi-analyzing-title"]?.[language] ?? "Fluxi está analizando tu producto"}
      </h2>
      <p className="text-sm text-[#64748B] text-center mb-10 max-w-md">
        {dictionary["fluxi-analyzing-subtitle"]?.[language] ?? "Estamos encontrando el diseño y las secciones más efectivas para tu tipo de producto"}
      </p>

      {/* Animated steps */}
      <div className="w-full max-w-sm space-y-3">
        {analysisSteps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === activeStep;
          const isDone = index < activeStep;

          return (
            <div
              key={index}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-500 ${
                isActive
                  ? 'bg-[#F5F8FF] border border-[#3E3EFF]/20 shadow-sm'
                  : isDone
                    ? 'bg-[#F0FDF4] border border-green-200'
                    : 'bg-[#F8FAFC] border border-transparent'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                  isActive
                    ? 'bg-[#3E3EFF] text-white'
                    : isDone
                      ? 'bg-green-500 text-white'
                      : 'bg-[#E2E8F0] text-gray-400'
                }`}
              >
                {isDone ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span
                className={`text-sm font-medium transition-all duration-500 ${
                  isActive ? 'text-[#3E3EFF]' : isDone ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
              {isActive && (
                <div className="ml-auto">
                  <Loader size={16} color="#3E3EFF" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
